// Character appearance settings for Unity character customization
// Schema v2 — wire contract per Documentation/ai-map/features/character-builder-overhaul/design.md §3.
// Field names ARE the wire names (Unity JsonUtility parses them verbatim).

import { DEFAULT_GESTURE_LOADOUT, sanitizeGestureLoadout } from './gestures';

export const APPEARANCE_SCHEMA_VERSION = 2;

export interface ColorRGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

export interface AppearanceSettings {
  // Schema version (2). Old payloads without it parse as legacy and merge into defaults.
  SchemaVersion: number;

  skinColor: ColorRGBA;
  hairColor: ColorRGBA;
  eyeColor: ColorRGBA;

  // Outfit PRESET (1-6 = Modern Civilian sets, 0 = base/nude); per-slot overrides below win
  outfitIndex: number;

  // Hair (0-9 = base styles, 10-15 = outfit-specific)
  hairStyle: number;

  // Facial hair (0 = none, 1-10 = styles)
  facialHairStyle: number;

  // Accessories (0 = none)
  hatIndex: number;        // 0-15
  glassesIndex: number;    // 0-13
  backpackIndex: number;   // 0-9

  // Body morphs (existing)
  height: number;          // 0.85-1.15 uniform whole-character scale
  bodyScale: number;       // 0.85-1.2 X/Z width only
  muscleValue: number;     // 0-1, buff blendshape; 0.5 = neutral

  // Per-slot part overrides (§3.2; 0 = auto = derive from outfit/base)
  headIndex: number;       // 0-2
  eyebrowIndex: number;    // 0-10
  noseIndex: number;       // 0-11
  earIndex: number;        // 0-10
  teethIndex: number;      // 0-10
  torsoIndex: number;      // 0-18
  hipsIndex: number;       // 0-14
  legsIndex: number;       // 0-8
  feetIndex: number;       // 0-14
  armUpperIndex: number;   // 0-9
  armLowerIndex: number;   // 0-10
  handsIndex: number;      // 0-2
  shoulderIndex: number;   // 0-2 (shoulder pads, 0 = none)

  // Body-shape blendshape fields (§3.3)
  buildValue: number;      // 0-1 bipolar: 0.5 neutral, <0.5 skinny, >0.5 heavy
  feminineValue: number;   // 0-1, masculine->feminine

  // Bone-morph fields (§3.4; neutral 1.0)
  legLength: number;       // 0.9-1.1
  armLength: number;       // 0.9-1.1
  thighSize: number;       // 0.85-1.25
  bicepsSize: number;      // 0.85-1.25
  shoulderWidth: number;   // 0.9-1.15
  torsoWidth: number;      // 0.9-1.2
  headSize: number;        // 0.9-1.1

  // Gesture loadout: ordered gesture ids (data/gestureCatalog.json), max 8.
  // Slot order = in-room emote bar order = hotkeys 1-8. Rides inside the
  // appearanceData JSON string, so the backend needs no schema change.
  gestures: string[];
}

export const DEFAULT_APPEARANCE: AppearanceSettings = {
  SchemaVersion: APPEARANCE_SCHEMA_VERSION,
  skinColor: { r: 0.87, g: 0.72, b: 0.58, a: 1 },
  hairColor: { r: 0.15, g: 0.1, b: 0.05, a: 1 },
  eyeColor: { r: 0.4, g: 0.25, b: 0.1, a: 1 },
  outfitIndex: 1,
  hairStyle: 0,
  facialHairStyle: 0,
  hatIndex: 0,
  glassesIndex: 0,
  backpackIndex: 0,
  height: 1,
  bodyScale: 1,
  muscleValue: 0.5,
  headIndex: 0,
  eyebrowIndex: 0,
  noseIndex: 0,
  earIndex: 0,
  teethIndex: 0,
  torsoIndex: 0,
  hipsIndex: 0,
  legsIndex: 0,
  feetIndex: 0,
  armUpperIndex: 0,
  armLowerIndex: 0,
  handsIndex: 0,
  shoulderIndex: 0,
  buildValue: 0.5,
  feminineValue: 0,
  legLength: 1,
  armLength: 1,
  thighSize: 1,
  bicepsSize: 1,
  shoulderWidth: 1,
  torsoWidth: 1,
  headSize: 1,
  gestures: [...DEFAULT_GESTURE_LOADOUT],
};

// ── Part catalog (§6.1 / §2 verified asset manifest) ──
// Count = highest selectable index. For override/accessory slots 0 means
// auto/none, so the value range is 0..count. Hair is the exception: all 16
// values 0-15 are real styles (no "none").
export type PartSlot =
  | 'head' | 'eyebrow' | 'nose' | 'ear' | 'teeth'
  | 'torso' | 'hips' | 'legs' | 'feet'
  | 'armUpper' | 'armLower' | 'hands' | 'shoulder'
  | 'hat' | 'glasses' | 'backpack'
  | 'hair' | 'facialHair';

export const PART_CATALOG: Record<PartSlot, number> = {
  head: 2,
  eyebrow: 10,
  nose: 11,
  ear: 10,
  teeth: 10,
  torso: 18,
  hips: 14,
  legs: 8,
  feet: 14,
  armUpper: 9,
  armLower: 10,
  hands: 2,
  shoulder: 2,
  hat: 15,
  glasses: 13,
  backpack: 9,
  hair: 16,
  facialHair: 10,
};

// Appearance field carrying each part slot's index (wire names per §3)
export type PartField =
  | 'headIndex' | 'eyebrowIndex' | 'noseIndex' | 'earIndex' | 'teethIndex'
  | 'torsoIndex' | 'hipsIndex' | 'legsIndex' | 'feetIndex'
  | 'armUpperIndex' | 'armLowerIndex' | 'handsIndex' | 'shoulderIndex'
  | 'hatIndex' | 'glassesIndex' | 'backpackIndex'
  | 'hairStyle' | 'facialHairStyle';

export const PART_FIELD_MAP: Record<PartSlot, PartField> = {
  head: 'headIndex',
  eyebrow: 'eyebrowIndex',
  nose: 'noseIndex',
  ear: 'earIndex',
  teeth: 'teethIndex',
  torso: 'torsoIndex',
  hips: 'hipsIndex',
  legs: 'legsIndex',
  feet: 'feetIndex',
  armUpper: 'armUpperIndex',
  armLower: 'armLowerIndex',
  hands: 'handsIndex',
  shoulder: 'shoulderIndex',
  hat: 'hatIndex',
  glasses: 'glassesIndex',
  backpack: 'backpackIndex',
  hair: 'hairStyle',
  facialHair: 'facialHairStyle',
};

// ── Morph specs (§6.1; ranges from §3.1/§3.3/§3.4) ──
export type MorphKey =
  | 'height' | 'bodyScale' | 'muscleValue'
  | 'buildValue' | 'feminineValue'
  | 'legLength' | 'armLength' | 'thighSize' | 'bicepsSize'
  | 'shoulderWidth' | 'torsoWidth' | 'headSize';

export interface MorphSpec {
  key: MorphKey;
  label: string;
  min: number;
  max: number;
  step: number;
  default: number;
}

export const MORPH_SPECS: MorphSpec[] = [
  { key: 'height', label: 'Height', min: 0.85, max: 1.15, step: 0.01, default: 1 },
  { key: 'bodyScale', label: 'Width', min: 0.85, max: 1.2, step: 0.01, default: 1 },
  { key: 'muscleValue', label: 'Muscle', min: 0, max: 1, step: 0.01, default: 0.5 },
  { key: 'buildValue', label: 'Build', min: 0, max: 1, step: 0.01, default: 0.5 },
  { key: 'feminineValue', label: 'Femininity', min: 0, max: 1, step: 0.01, default: 0 },
  { key: 'legLength', label: 'Leg Length', min: 0.9, max: 1.1, step: 0.01, default: 1 },
  { key: 'armLength', label: 'Arm Length', min: 0.9, max: 1.1, step: 0.01, default: 1 },
  { key: 'thighSize', label: 'Thigh Size', min: 0.85, max: 1.25, step: 0.01, default: 1 },
  { key: 'bicepsSize', label: 'Biceps Size', min: 0.85, max: 1.25, step: 0.01, default: 1 },
  { key: 'shoulderWidth', label: 'Shoulder Width', min: 0.9, max: 1.15, step: 0.01, default: 1 },
  { key: 'torsoWidth', label: 'Torso Width', min: 0.9, max: 1.2, step: 0.01, default: 1 },
  { key: 'headSize', label: 'Head Size', min: 0.9, max: 1.1, step: 0.01, default: 1 },
];

// Morph floats where a wire value of 0 means "unset" (§3: 0 = unset -> neutral).
// muscleValue/feminineValue are excluded: 0 is a legitimate in-range value.
const ZERO_UNSET_MORPHS: MorphKey[] = [
  'height', 'bodyScale', 'buildValue',
  'legLength', 'armLength', 'thighSize', 'bicepsSize',
  'shoulderWidth', 'torsoWidth', 'headSize',
];

export const SKIN_PRESETS: { color: ColorRGBA; label: string }[] = [
  { color: { r: 1.0, g: 0.87, b: 0.77, a: 1 }, label: 'Light' },
  { color: { r: 0.87, g: 0.72, b: 0.58, a: 1 }, label: 'Medium Light' },
  { color: { r: 0.76, g: 0.57, b: 0.42, a: 1 }, label: 'Medium' },
  { color: { r: 0.55, g: 0.38, b: 0.26, a: 1 }, label: 'Medium Dark' },
  { color: { r: 0.36, g: 0.22, b: 0.13, a: 1 }, label: 'Dark' },
];

export const HAIR_COLOR_PRESETS: { color: ColorRGBA; label: string }[] = [
  { color: { r: 0.1, g: 0.1, b: 0.1, a: 1 }, label: 'Black' },
  { color: { r: 0.35, g: 0.2, b: 0.1, a: 1 }, label: 'Brown' },
  { color: { r: 0.85, g: 0.65, b: 0.35, a: 1 }, label: 'Blonde' },
  { color: { r: 0.6, g: 0.2, b: 0.1, a: 1 }, label: 'Red' },
  { color: { r: 0.5, g: 0.5, b: 0.5, a: 1 }, label: 'Gray' },
  { color: { r: 0.95, g: 0.95, b: 0.95, a: 1 }, label: 'White' },
];

export const EYE_COLOR_PRESETS: { color: ColorRGBA; label: string }[] = [
  { color: { r: 0.4, g: 0.25, b: 0.1, a: 1 }, label: 'Brown' },
  { color: { r: 0.2, g: 0.4, b: 0.7, a: 1 }, label: 'Blue' },
  { color: { r: 0.2, g: 0.5, b: 0.3, a: 1 }, label: 'Green' },
  { color: { r: 0.4, g: 0.4, b: 0.4, a: 1 }, label: 'Gray' },
  { color: { r: 0.45, g: 0.35, b: 0.2, a: 1 }, label: 'Hazel' },
];

export const HAIR_STYLE_COUNT = PART_CATALOG.hair;       // 10 base species + 6 outfit-specific
export const OUTFIT_COUNT = 6;                            // 6 Modern Civilian outfit sets
export const FACIAL_HAIR_COUNT = PART_CATALOG.facialHair;
export const HAT_COUNT = PART_CATALOG.hat;
export const GLASSES_COUNT = PART_CATALOG.glasses;
export const BACKPACK_COUNT = PART_CATALOG.backpack;

// Utility functions
export function colorToHex(color: ColorRGBA): string {
  const r = Math.round(color.r * 255).toString(16).padStart(2, '0');
  const g = Math.round(color.g * 255).toString(16).padStart(2, '0');
  const b = Math.round(color.b * 255).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

export function hexToColor(hex: string): ColorRGBA {
  const value = hex.replace('#', '');
  return {
    r: parseInt(value.slice(0, 2), 16) / 255,
    g: parseInt(value.slice(2, 4), 16) / 255,
    b: parseInt(value.slice(4, 6), 16) / 255,
    a: 1,
  };
}

export function colorsEqual(a: ColorRGBA, b: ColorRGBA): boolean {
  return Math.abs(a.r - b.r) < 0.01 &&
         Math.abs(a.g - b.g) < 0.01 &&
         Math.abs(a.b - b.b) < 0.01;
}

export function parseAppearanceData(json: string | undefined): AppearanceSettings {
  if (!json) return { ...DEFAULT_APPEARANCE };
  try {
    const parsed = JSON.parse(json) as Record<string, unknown> | null;
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_APPEARANCE };

    // Merge into defaults so old (v0/v1) saves pick up new fields at neutral.
    const merged = { ...DEFAULT_APPEARANCE } as unknown as Record<string, unknown>;
    for (const key of Object.keys(DEFAULT_APPEARANCE)) {
      const value = parsed[key];
      if (value !== undefined && value !== null) {
        merged[key] = value;
      }
    }

    const result = merged as unknown as AppearanceSettings;
    // Colors must stay objects even if a save carried a falsy value.
    result.skinColor = (parsed.skinColor as ColorRGBA) || DEFAULT_APPEARANCE.skinColor;
    result.hairColor = (parsed.hairColor as ColorRGBA) || DEFAULT_APPEARANCE.hairColor;
    result.eyeColor = (parsed.eyeColor as ColorRGBA) || DEFAULT_APPEARANCE.eyeColor;
    // 0-sentinel repair, mirroring Unity's ClampToValidRanges (§3).
    for (const spec of MORPH_SPECS) {
      if (ZERO_UNSET_MORPHS.includes(spec.key) && result[spec.key] === 0) {
        result[spec.key] = spec.default;
      }
    }
    // Gesture loadout: drop unknown/unpickable ids; missing/empty -> default.
    result.gestures = sanitizeGestureLoadout(parsed.gestures);
    // Backpacks are disabled product-wide (ABAC parts render at the face) —
    // strip them from old saves so re-saving heals the character.
    result.backpackIndex = 0;
    result.SchemaVersion = APPEARANCE_SCHEMA_VERSION;
    return result;
  } catch {
    return { ...DEFAULT_APPEARANCE };
  }
}

export function serializeAppearanceData(appearance: AppearanceSettings): string {
  // Wire epsilon law (§3.3): 0 is the "unset" sentinel for buildValue, so a real
  // full-skinny value must serialize as 0.01 — otherwise it reloads as neutral 0.5.
  const buildValue = appearance.buildValue < 0.01 ? 0.01 : appearance.buildValue;
  return JSON.stringify({ ...appearance, buildValue, SchemaVersion: APPEARANCE_SCHEMA_VERSION });
}
